import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateOrderRequest,
    QuickSalePayment,
    QuickSaleRequest,
    SalesOrder,
} from '@/lib/api/sales-order-api'
import type { CustomerDisplaySyncRequest } from '@/lib/schemas/customer-display-event-schemas'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import { posDisplayPublisher } from '@/lib/pos-display-publisher'
import { getDisplayUserId } from '@/components/pos/pos-display-picker'
import { orderKeys } from '@/hooks/use-sales-order-query'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

// Two POS submit modes:
//   - 'save':    create order, status=new, no payment. Staff/accounting
//                handles next steps (confirm, assign driver, start delivery)
//                from the order detail page.
//   - 'pay_now': full cashier flow — create + confirm + stock-out + pay in one
//                atomic call, then open invoice popup.
export type PosCheckoutMode = 'pay_now' | 'save'

export interface PosCheckoutInput {
    order: CreateOrderRequest
    mode: PosCheckoutMode
    paymentMethod: 'cash' | 'bank_transfer'
    paymentAmount: number
    overpayAction: 'change' | 'credit'
    /** Phần khách đưa dư ghi vào số dư khách ("Tính vào công nợ"); 0/bỏ trống = trả tiền thừa. */
    overpayCredit?: number
    /** M-175: tiền khách đưa theo từng hình thức; thiếu thì phần còn lại thành
     *  nợ của đơn. Bỏ trống = thu đủ bằng `paymentMethod` (màn cũ). */
    payments?: QuickSalePayment[]
}

export function usePosCheckout() {
    const qc = useQueryClient()

    return useMutation({
        mutationFn: async (input: PosCheckoutInput): Promise<SalesOrder> => {
            // Save mode: just create order (status = new). Confirm / delivery
            // happen later from the order detail page.
            if (input.mode === 'save') {
                const createRes = await salesOrderApi.create(input.order)
                const order = createRes.data
                if (!order) throw new Error(m.pos_checkout_create_order_error())
                return order
            }

            // Pay-now mode: single API call that creates + confirms + stocks-out + pays
            const displayUserId = getDisplayUserId()
            const quickReq: QuickSaleRequest = {
                ...input.order,
                payment_method: input.paymentMethod,
                display_user_id: displayUserId,
                ...(input.overpayCredit && input.overpayCredit > 0
                    ? { overpay_credit: input.overpayCredit }
                    : {}),
                ...(input.payments ? { payments: input.payments } : {}),
            }
            const res = await salesOrderApi.quickCreate(quickReq)
            if (!res.data) throw new Error(m.pos_checkout_payment_error())
            if (displayUserId) syncCheckout(res.data, input, displayUserId)
            return res.data
        },
        onSuccess: (_data, input) => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            if (input.mode === 'save') {
                toast.success(
                    m.toast_pos_order_saved({
                        code: _data.order_code,
                    }),
                )
            } else {
                toast.success(m.toast_pos_order_success())
            }
        },
        onError: (err) => {
            // BE trả error envelope với message tiếng Việt (vd:
            // "Không đủ tồn kho: Trà Oolong (cần 50, còn 10)"). Surface
            // server message + trace ID cho user qua formatApiError thay vì
            // generic "Đặt hàng thất bại".
            const payload = formatApiError(err, m.toast_pos_order_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

function syncCheckout(
    order: SalesOrder,
    input: PosCheckoutInput,
    displayUserId: string,
) {
    const request: CustomerDisplaySyncRequest = {
        display_user_id: displayUserId,
        type: 'checkout',
        data: {
            order_id: order.id,
            order_code: order.order_code,
            total: order.total_amount,
            paid: input.paymentAmount,
            // Phần dư đã ghi vào công nợ khách thì không còn tiền thừa trả lại.
            change:
                input.overpayCredit && input.overpayCredit > 0
                    ? 0
                    : Math.max(0, input.paymentAmount - order.total_amount),
            method: input.paymentMethod,
        },
    }
    const publish = (replace = false) => {
        void posDisplayPublisher
            .publish(request, { replace })
            .catch((error: unknown) => {
                const message = formatApiError(
                    error,
                    m.pos_display_sync_failed(),
                )
                toast.error(message.title, {
                    description: message.description,
                    action: {
                        label: m.pos_display_sync_retry(),
                        onClick: () => publish(true),
                    },
                })
            })
    }
    publish()
}
